import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { useMediaQuery, Box, useTheme, Paper, Skeleton, TextField } from "@mui/material";
import { ApartmentOutlined, PersonOutline, SystemUpdateAlt } from "@mui/icons-material";
import {
  Button,
  ChoiceToggle,
  CountrySelect,
  FieldWrapper,
  OptionCardGroup,
  PageHeader,
  SectionHeader,
} from "@components/ui_v2";
import { CardResumenCotizacion, SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { usePostDocumentQuotationTableMutation } from "@api/documents";
import { ApiError, TaxSystemConfigResponse } from "@interfaces/index";
import {
  calculateAmountMonthly,
  formatCurrency,
  buildTermOptions,
  getClosestTerm,
  formatCurrencyShort,
} from "@helpers/funciones";
import { PATHS_TOFU_V2 } from "@helpers/constantes";
import { PHONE_NUMBER } from "@utils/expreciones";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";

type PersonType = "PM" | "PFAE";

interface FormValues {
  personType: PersonType;
  amount: number;
  term: number;
  trade_name: string;
  country_code: string;
  phone: string;
}

const EMPTY_CONFIG: TaxSystemConfigResponse = {
  id: "",
  code: "",
  product_id: "",
  minAmount: 0,
  maxAmount: 0,
  stepAmount: 0,
  minTerm: 0,
  maxTerm: 0,
  stepTerm: 0,
  defaultAmount: 0,
  defaultTerm: 0,
  tax_system_type: "",
};

const validationSchema = yup.object().shape({
  trade_name: yup.string().trim().required("Campo requerido"),
  country_code: yup.string().required("Campo requerido"),
  phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
});

const CotizadorFlux = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [postGenerateQuotationTable] = usePostDocumentQuotationTableMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAE, setConfigPFAE] = useState<TaxSystemConfigResponse | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
      personType: "PM",
      amount: 0,
      term: 0,
      trade_name: "",
      country_code: "+52",
      phone: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (!cotizadorConfig.product_id || !values.amount || !values.term || !monthlyRate) {
        return;
      }
      const sourceCode = sessionStorage.getItem("source_code");

      let urlPortal = `${PATHS_TOFU_V2.crearCuenta.path}?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${values.amount}&loan_term=${values.term}&tax_system_type=${cotizadorConfig.tax_system_type}`;
      // TODO: `CrearCuenta` (v2) todavía no lee los datos de contacto; se mandan para que los tome
      // en cuanto se conecte. `encodeURIComponent` porque la lada trae `+`.
      urlPortal += `&trade_name=${encodeURIComponent(values.trade_name.trim())}&code_phone_number=${encodeURIComponent(values.country_code)}&phone_number=${values.phone}`;
      if (sourceCode) {
        urlPortal += `&source_code=${sourceCode}`;
      }
      navigate(urlPortal);
    },
  });

  const { values, setFieldValue } = formik;

  const cotizadorConfig = (values.personType === "PM" ? configPM : configPFAE) ?? EMPTY_CONFIG;
  const termOptions = useMemo(
    () => buildTermOptions(cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm),
    [cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm],
  );

  const monthlyRate = useMemo(
    () =>
      values.amount && values.term
        ? calculateAmountMonthly(values.amount, values.term, interfaceConfig?.interest_rate)
        : 0,
    [values.amount, values.term, interfaceConfig?.interest_rate],
  );

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [responsePM, responsePFAE] = await Promise.all([
        getTaxSystemConfig("FLXPM").unwrap(),
        getTaxSystemConfig("FLXPF").unwrap(),
      ]);
      setConfigPM(responsePM);
      setConfigPFAE(responsePFAE);
    } catch (error) {
      // Mismo alcance que el legacy: sin config la pantalla queda en ceros, sin modal de error.
      Sentry.captureException(error, { extra: { component: "CotizadorFlux-initial_fetch" } });
    } finally {
      setIsLoading(false);
    }
  }, [getTaxSystemConfig]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  // Al cambiar de régimen (o al llegar la config) se vuelve a los defaults de ese régimen.
  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } = cotizadorConfig;

    if (minTerm < maxTerm && stepTerm > 0) {
      setFieldValue("amount", defaultAmount);
      setFieldValue("term", getClosestTerm(buildTermOptions(minTerm, maxTerm, stepTerm), defaultTerm));
    }
  }, [cotizadorConfig, setFieldValue]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const genarateQuotationTable = async () => {
    try {
      setIsLoading(true);
      const responseBase64 = await postGenerateQuotationTable({
        amount: values.amount,
        term: values.term,
        company: interfaceConfig?.name || "",
        credit_type: "credito_simple",
      }).unwrap();

      const byteCharacters = atob(String(responseBase64));
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const file = new Blob([byteArray], { type: "application/pdf" });

      const fileURL = URL.createObjectURL(file);

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = "tabla_amortizacion.pdf";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const monthlyText = isNaN(monthlyRate) || !isFinite(monthlyRate) ? "$0.00" : formatCurrency(monthlyRate);
  const bloqueado = isLoading || formik.isSubmitting;

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="Cotización"
        title="Estima tu financiamiento"
        subtitle="Ajusta monto y plazo para ver una estimación de pago."
      />

      <Paper variant="lendia_paper">
        <SectionHeader variant="cotizador" label="Régimen fiscal" first />
        {!isLoading ? (
          <OptionCardGroup
            layout="grid"
            value={values.personType}
            onChange={(value) => setFieldValue("personType", value)}
            columns={isMobileOrTablet ? 1 : 2}
            options={[
              {
                value: "PM",
                label: "Persona Moral",
                sub: "Empresa constituida (SA, SAPI, SC, Etc.)",
                icon: <ApartmentOutlined fontSize="small" />,
                testId: "cotizador-flux-regimen-pm",
              },
              {
                value: "PFAE",
                label: "Persona Física Act. Emp.",
                sub: "Empresa PFAE",
                icon: <PersonOutline fontSize="small" />,
                testId: "cotizador-flux-regimen-pfae",
              },
            ]}
          />
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: isMobileOrTablet ? "1fr" : "repeat(2, 1fr)", gap: 1.5 }}>
            <Skeleton variant="rounded" animation="wave" height={84} />
            <Skeleton variant="rounded" animation="wave" height={84} />
          </Box>
        )}

        <SectionHeader
          variant="cotizador"
          label="Monto"
          meta={`${formatCurrencyShort(cotizadorConfig.minAmount, true)} – ${formatCurrencyShort(cotizadorConfig.maxAmount, true)} MXN`}
        />
        <SliderCotizador
          value={values.amount}
          onChange={(value) => setFieldValue("amount", value)}
          min={cotizadorConfig.minAmount}
          max={cotizadorConfig.maxAmount}
          step={cotizadorConfig.stepAmount}
          valueText={formatCurrencyShort(values.amount, true)}
          unit="MXN"
          minLabel={formatCurrencyShort(cotizadorConfig.minAmount, true)}
          maxLabel={formatCurrencyShort(cotizadorConfig.maxAmount, true)}
          loading={isLoading}
          testId="cotizador-flux-monto-slider"
        />

        <SectionHeader variant="cotizador" label="Plazo" />
        {!isLoading ? (
          <ChoiceToggle
            variant="cotizador"
            value={values.term}
            onChange={(value) => setFieldValue("term", value)}
            options={termOptions.map((term) => ({ value: term, label: `${term} meses` }))}
            testId="cotizador-flux-plazo"
          />
        ) : (
          <Skeleton variant="rounded" animation="wave" height={50} />
        )}

        <SectionHeader variant="cotizador" label="Tus datos de contacto" meta="Para enviarte tu oferta" />
        <Box
          sx={{ display: "grid", gridTemplateColumns: isMobileOrTablet ? "1fr" : "repeat(2, 1fr)", gap: "12px 14px" }}
        >
          <FieldWrapper
            label="Nombre comercial"
            hint="Como te conocen tus clientes; puede ser distinto a tu razón social."
            error={(formik.touched.trade_name && formik.errors.trade_name) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="trade_name"
              placeholder="Textiles del Norte"
              value={values.trade_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={bloqueado}
              inputProps={{ "data-testid": "cotizador-flux-nombre-comercial-input" }}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Teléfono celular"
            hint="A este número te contactamos y enviamos tu oferta por WhatsApp."
            error={(formik.touched.phone && formik.errors.phone) || ""}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={values.country_code}
                onChange={(value) => setFieldValue("country_code", value)}
                disabled={bloqueado}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="phone"
                placeholder="55 1234 5678"
                value={values.phone}
                onChange={(e) => setFieldValue("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                onBlur={formik.handleBlur}
                disabled={bloqueado}
                inputProps={{ "data-testid": "cotizador-flux-telefono-input" }}
              />
            </Box>
          </FieldWrapper>
        </Box>
      </Paper>

      <CardResumenCotizacion
        label="Pago mensual estimado*"
        amount={monthlyText}
        caption={`MXN / mes durante ${values.term || 0} meses`}
        disclaimer="*Esta simulación es para fines demostrativos y está sujeta a aprobación. Las condiciones como la tasa, el monto del crédito o la comisión por apertura podrían cambiar dependiendo del perfil y la situación actual del solicitante."
        actions={
          <>
            <Button
              tone="hero"
              withArrow
              onClick={formik.submitForm}
              disabled={bloqueado}
              data-testid="cotizador-flux-continue-button"
            >
              Solicitar financiamiento
            </Button>
            {/* No está en el diseño: se conserva porque el cotizador legacy ya la ofrecía. */}
            <Button
              tone="link"
              startIcon={<SystemUpdateAlt sx={{ fontSize: "16px !important" }} />}
              onClick={genarateQuotationTable}
              disabled={bloqueado}
              data-testid="cotizador-flux-tabla-amortizacion-button"
            >
              Descargar tabla de amortización informativa
            </Button>
          </>
        }
      />
    </Box>
  );
};
export default CotizadorFlux;
